import { Heart, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { openCookieSettingsModal } from "@/lib/cookie-consent";

const footerLinks = {
  Producto: [
    { label: "Funcionalidades", href: "/#features" },
    { label: "Precios", href: "/#pricing" },
    { label: "Seguridad", href: "/#security" },
    { label: "FAQ", href: "/#faq" },
  ],
  Empresa: [
    { label: "Quiénes somos", to: "/quienes-somos" },
    { label: "Contacto", to: "/contacto" },
    { label: "hola@ekklesiaonline.app", mailto: "hola@ekklesiaonline.app" },
  ],
  Legal: [
    { label: "Privacidad", to: "/privacidad" },
    { label: "Términos de uso", to: "/terminos-condiciones" },
    { label: "Cookies", to: "/cookies" },
    { label: "Ajustes de cookies", cookieSettings: true },
  ],
};

const bottomLegal = [
  { label: "Privacidad", to: "/privacidad" },
  { label: "Términos", to: "/terminos-condiciones" },
  { label: "Cookies", to: "/cookies" },
];

export default function Footer() {
  return (
    <footer
      data-testid="footer"
      className="relative bg-[#F2F1F7] pb-10 pt-10 md:pb-14 md:pt-12"
    >
      {/* Mismo ancho horizontal que el bloque «¿Quieres verlo funcionando?» (CTASection: mx-4 md:mx-8) */}
      <div className="mx-3 sm:mx-4 md:mx-8">
        <div
          className="overflow-hidden rounded-[24px] border border-violet-light/80 bg-white shadow-[0_12px_48px_-16px_rgba(26,17,53,0.12),0_4px_16px_-4px_rgba(74,58,138,0.08)]"
        >
        <div className="px-6 py-10 md:px-12 md:py-14 lg:py-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-14">
            {/* Marca */}
            <div className="lg:col-span-5 xl:col-span-4">
              <Link
                to="/"
                className="group inline-flex items-center gap-3"
                data-testid="footer-logo"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-fuerte shadow-[0_8px_24px_-6px_rgba(74,58,138,0.3)] ring-1 ring-violet-fuerte/15 transition-[box-shadow] duration-300 group-hover:shadow-[0_10px_28px_-6px_rgba(74,58,138,0.38)]">
                  <span className="font-display text-lg font-bold leading-none text-white">E</span>
                </div>
                <span className="font-display text-xl font-bold tracking-tight text-ink">
                  Ekklesia
                </span>
              </Link>

              <p className="mt-6 max-w-[22rem] text-[17px] leading-relaxed text-ink-2">
                El software de gestión que la iglesia merece. Fácil, seguro y accesible.
              </p>

              <div
                className="mt-8 h-px max-w-[12rem] bg-gradient-to-r from-gold/60 via-violet-medio/25 to-transparent"
                aria-hidden
              />

              <p className="mt-6 text-sm leading-relaxed text-ink-3">
                Diseñado para pastores, equipos y tesoreros que necesitan claridad, no más hojas de
                cálculo sueltas.
              </p>
            </div>

            {/* Navegación */}
            <div className="grid grid-cols-2 gap-10 sm:gap-12 md:grid-cols-3 lg:col-span-7 xl:col-span-8">
              {Object.entries(footerLinks).map(([title, links]) => (
                <div key={title}>
                  <h4 className="font-display text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                    {title}
                  </h4>
                  <ul className="mt-5 space-y-3.5">
                    {links.map((link) => (
                      <li key={link.label}>
                        {link.cookieSettings ? (
                          <button
                            type="button"
                            onClick={() => openCookieSettingsModal()}
                            data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="inline-block text-[15px] text-ink-2 transition-colors duration-200 hover:text-violet-fuerte"
                          >
                            {link.label}
                          </button>
                        ) : link.mailto ? (
                          <a
                            href={`mailto:${link.mailto}`}
                            className="flex items-start gap-1.5 text-[13px] text-ink-3 transition-colors duration-200 hover:text-violet-fuerte"
                          >
                            <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                            <span className="break-all">{link.label}</span>
                          </a>
                        ) : link.to ? (
                          <Link
                            to={link.to}
                            data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="inline-block text-[15px] text-ink-2 transition-colors duration-200 hover:text-violet-fuerte"
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <a
                            href={link.href}
                            data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="inline-block text-[15px] text-ink-2 transition-colors duration-200 hover:text-violet-fuerte"
                          >
                            {link.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Barra inferior — divisor + copyright / legales (estilo referencia) */}
        <div className="border-t border-violet-light/70 px-6 py-6 md:px-12 md:py-7">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <p className="text-sm text-ink-3">
              &copy; {new Date().getFullYear()} Ekklesia. Todos los derechos reservados.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
              <nav className="flex flex-wrap items-center gap-x-1" aria-label="Legal">
                {bottomLegal.map((item, i) => (
                  <span key={item.label} className="inline-flex items-center">
                    {i > 0 ? (
                      <span className="mx-3 text-ink-4 select-none" aria-hidden>
                        ·
                      </span>
                    ) : null}
                    {item.to ? (
                      <Link
                        to={item.to}
                        className="text-sm font-medium text-ink-2 underline decoration-ink-4/50 underline-offset-4 transition-colors duration-200 hover:text-violet-fuerte hover:decoration-violet-fuerte/40"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        className="text-sm font-medium text-ink-2 underline decoration-ink-4/50 underline-offset-4 transition-colors duration-200 hover:text-violet-fuerte hover:decoration-violet-fuerte/40"
                      >
                        {item.label}
                      </a>
                    )}
                  </span>
                ))}
              </nav>

              <p className="flex items-center gap-2 text-sm text-ink-3 sm:border-l sm:border-violet-light sm:pl-8">
                <span>Hecho con</span>
                <Heart
                  className="h-4 w-4 shrink-0 text-gold"
                  fill="currentColor"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <span>para la iglesia</span>
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </footer>
  );
}
